import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import firebase from 'src/utils/firebase';
import { getActiveShopId } from 'src/utils/shopContext';
import { socketClient } from '../index';

export const currentTechnicianScope = () => {
  const uid = firebase.auth().currentUser?.uid;
  return uid ? `${uid}:${getActiveShopId() || ''}` : null;
};

// Work Order pages load technicians once and may mount during an automatic socket
// reconnect. Reads can wait for that captured connection; mutations never queue.
const waitForTechnicianReadConnection = (connection, idToken, scope, signal) =>
  new Promise((resolve, reject) => {
    const cleanup = () => {
      clearTimeout(timeoutId);
      connection.off('connect', handleConnect);
      connection.off('connect_error', handleError);
      signal.removeEventListener('abort', handleAbort);
    };
    const fail = (message) => {
      cleanup();
      reject(new Error(message));
    };
    const handleConnect = () => {
      if (signal.aborted || currentTechnicianScope() !== scope
        || socketClient.socket !== connection || socketClient.authToken !== idToken
        || Number(socketClient.shopId) !== Number(getActiveShopId())) {
        fail('Account or connection changed. Refresh technicians.');
        return;
      }
      if (!connection.connected) return;
      cleanup();
      resolve();
    };
    const handleError = () => fail('Unable to connect while loading technicians.');
    const handleAbort = () => fail('Technician request cancelled.');
    const timeoutId = setTimeout(() => fail('Timed out while connecting to load technicians.'), 10000);
    connection.on('connect', handleConnect);
    connection.on('connect_error', handleError);
    signal.addEventListener('abort', handleAbort);
    if (signal.aborted) handleAbort();
  });

const emitScopedTechniciansRequest = ({
  emitEvent, payload, timeoutMessage, scope, signal, connection
}) => new Promise((resolve, reject) => {
  const cleanup = () => {
    clearTimeout(timeoutId);
    connection.off('getTechnicians', handleSuccess);
    connection.off('getTechniciansError', handleError);
    connection.off('disconnect', handleDisconnect);
    signal.removeEventListener('abort', handleAbort);
  };
  const fail = (message) => {
    cleanup();
    reject(new Error(message));
  };
  const handleSuccess = (response) => {
    if (response?.requestId !== payload.requestId) return;
    if (currentTechnicianScope() !== scope) {
      fail('Account changed. Refresh technicians.');
      return;
    }
    const technicians = Array.isArray(response) ? response : response?.technicians;
    if (!Array.isArray(technicians)) {
      fail('Unable to read technician status. Refresh and try again.');
      return;
    }
    cleanup();
    resolve(technicians);
  };
  const handleError = (response) => {
    if (response?.requestId !== payload.requestId) return;
    fail(currentTechnicianScope() === scope
      ? response?.message || timeoutMessage
      : 'Account changed. Refresh technicians.');
  };
  const handleAbort = () => fail('Technician request cancelled.');
  const handleDisconnect = () => fail('Realtime connection changed. Refresh status before trying again.');
  const timeoutId = setTimeout(() => fail(timeoutMessage), 10000);
  connection.on('getTechnicians', handleSuccess);
  connection.on('getTechniciansError', handleError);
  connection.on('disconnect', handleDisconnect);
  signal.addEventListener('abort', handleAbort);
  if (signal.aborted) {
    handleAbort();
    return;
  }
  try {
    // Send synchronously on this authenticated connection; do not queue a mutation
    // through SocketClient.emit across reconnects or account changes.
    connection.emit(emitEvent, payload);
  } catch (error) {
    fail(error.message || timeoutMessage);
  }
});

const initialState = {
  technicians: [],
  loading: false,
  error: null,
  scope: null,
  requestId: null
};

const technicianRequest = (event, timeoutMessage, buildPayload = (value) => value || {}) =>
  createAsyncThunk(`technicians/${event === 'getTechnicians' ? 'fetchTechnicians' : event}`, async (value, { rejectWithValue, requestId, signal }) => {
    const scope = currentTechnicianScope();
    try {
      if (!scope) throw new Error('Sign in to manage technicians.');
      const idToken = await firebase.auth().currentUser.getIdToken();
      // Never send an old form or response into a new account/shop after token hydration.
      if (signal.aborted || currentTechnicianScope() !== scope) {
        throw new Error('Account changed. Refresh technicians.');
      }
      const connection = socketClient.socket;
      if (event === 'getTechnicians' && connection && !connection.connected) {
        await waitForTechnicianReadConnection(connection, idToken, scope, signal);
      }
      // Recheck after any read-only wait; its connection/account may have changed.
      if (signal.aborted || currentTechnicianScope() !== scope
        || socketClient.socket !== connection || !connection?.connected || socketClient.authToken !== idToken
        || Number(socketClient.shopId) !== Number(getActiveShopId())) {
        throw new Error('Realtime connection is not ready for this account. Refresh status after reconnecting.');
      }
      const technicians = await emitScopedTechniciansRequest({
        emitEvent: event,
        payload: { ...buildPayload(value), idToken, requestId },
        timeoutMessage, scope, signal, connection
      });
      if (currentTechnicianScope() !== scope) {
        throw new Error('Account changed. Refresh technicians.');
      }
      return technicians;
    } catch (error) {
      return rejectWithValue(error.message || timeoutMessage);
    }
  }, {
    getPendingMeta: () => ({ scope: currentTechnicianScope() }),
    // Prevent repeated saves/retries, including before React paints disabled buttons.
    condition: (_, { getState }) => event === 'getTechnicians'
      || !getState().technicians?.loading
  });

export const fetchTechnicians = technicianRequest('getTechnicians', 'Timed out while loading technicians');
export const createTechnician = technicianRequest('createTechnician', 'Timed out while creating technician. Retry this form to reuse the same save request, or refresh technicians before starting another create.');
export const editTechnician = technicianRequest('editTechnician', 'Timed out while updating technician');
export const deleteTechnician = technicianRequest('deleteTech', 'Timed out while deleting technician', (id) => ({ id }));
export const retryTechnicianPhoneSetup = technicianRequest('retryTechnicianPhoneSetup', 'Timed out while retrying phone setup. Refresh status before trying again.', (id) => ({ id }));

const slice = createSlice({
  name: 'technicians',
  initialState,
  reducers: {
    clearTechnicians: () => initialState
  },
  extraReducers: (builder) => {
    [fetchTechnicians, createTechnician, editTechnician, deleteTechnician, retryTechnicianPhoneSetup].forEach((request) => {
      builder
        .addCase(request.pending, (state, action) => {
          if (state.scope !== action.meta.scope) state.technicians = [];
          state.scope = action.meta.scope;
          state.requestId = action.meta.requestId;
          state.loading = true;
          state.error = null;
        })
        .addCase(request.fulfilled, (state, action) => {
          if (state.requestId !== action.meta.requestId) return;
          state.technicians = action.payload;
          state.loading = false;
          state.error = null;
          state.requestId = null;
        })
        .addCase(request.rejected, (state, action) => {
          if (state.requestId !== action.meta.requestId) return;
          state.loading = false;
          state.error = action.meta.aborted ? null : action.payload || action.error.message;
          state.requestId = null;
        });
    });
  }
});

export const { clearTechnicians } = slice.actions;
export const reducer = slice.reducer;
